import React from 'react';
import { Badge } from './ui/badge';
import { BadgeCheck, Phone, Shield } from 'lucide-react';

/**
 * VerifiedBadge Component
 * Displays seller verification status badge
 * 
 * @param {string} level - Verification level: 'none', 'phone', 'id', 'fully_verified'
 * @param {string} size - Badge size: 'sm', 'md', 'lg'
 * @param {boolean} showLabel - Whether to show text label
 * @param {string} className - Additional CSS classes
 */
const VerifiedBadge = ({ 
  level = 'none', 
  size = 'sm', 
  showLabel = true,
  className = '' 
}) => {
  if (level === 'none') return null;

  const getBadgeConfig = () => {
    switch (level) {
      case 'fully_verified':
        return {
          label: 'Verified Seller',
          shortLabel: 'Verified',
          icon: BadgeCheck,
          className: 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white border-0 hover:from-yellow-500 hover:to-orange-500',
          iconColor: 'text-white'
        };
      case 'id':
        return {
          label: 'ID Verified',
          shortLabel: 'ID ✓',
          icon: Shield,
          className: 'bg-green-500 text-white border-0 hover:bg-green-600',
          iconColor: 'text-white'
        };
      case 'phone':
        return {
          label: 'Phone Verified',
          shortLabel: 'Phone ✓',
          icon: Phone,
          className: 'bg-blue-500 text-white border-0 hover:bg-blue-600',
          iconColor: 'text-white'
        };
      default:
        return null;
    }
  };

  const config = getBadgeConfig();
  if (!config) return null;

  const Icon = config.icon;

  const sizeClasses = {
    sm: {
      badge: 'text-xs py-0.5 px-2',
      icon: 'h-3 w-3',
      gap: 'gap-1'
    },
    md: {
      badge: 'text-sm py-1 px-2.5',
      icon: 'h-4 w-4',
      gap: 'gap-1.5'
    },
    lg: {
      badge: 'text-base py-1.5 px-3',
      icon: 'h-5 w-5',
      gap: 'gap-2'
    }
  };

  const sizeConfig = sizeClasses[size] || sizeClasses.sm;

  return (
    <Badge 
      className={`inline-flex items-center ${sizeConfig.gap} ${sizeConfig.badge} ${config.className} ${className}`}
    >
      <Icon className={`${sizeConfig.icon} ${config.iconColor}`} />
      {showLabel && (
        <span>{size === 'sm' ? config.shortLabel : config.label}</span>
      )}
    </Badge>
  );
};

/**
 * VerifiedIcon Component
 * Displays just the verification icon (for tight spaces)
 */
export const VerifiedIcon = ({ level = 'none', size = 16, className = '' }) => {
  if (level === 'none') return null;

  const getIconConfig = () => {
    switch (level) {
      case 'fully_verified':
        return { icon: BadgeCheck, color: 'text-yellow-500' };
      case 'id':
        return { icon: Shield, color: 'text-green-500' };
      case 'phone':
        return { icon: Phone, color: 'text-blue-500' };
      default:
        return null;
    }
  };

  const config = getIconConfig();
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Icon 
      style={{ width: size, height: size }} 
      className={`${config.color} ${className}`} 
      title={level === 'fully_verified' ? 'Verified Seller' : level === 'id' ? 'ID Verified' : 'Phone Verified'}
    />
  );
};

export default VerifiedBadge;
